import { Combobox } from "@dashboard/components/Combobox";
import {
  CustomCell,
  CustomRenderer,
  getMiddleCenterBias,
  GridCellKind,
  ProvideEditorCallback,
} from "@glideapps/glide-data-grid";
import { Option } from "@saleor/macaw-ui-next";
import pick from "lodash/pick";
import React from "react";

export type DropdownCellGetSuggestionsFn = (text: string) => Promise<Option[]>;
export interface DropdownCellProps {
  readonly choices?: Option[];
  readonly update?: DropdownCellGetSuggestionsFn;
  readonly kind: "dropdown-cell";
  readonly value: Option | null;
  readonly allowCustomValues?: boolean;
  readonly emptyOption?: boolean;
  readonly pillColors?: Record<string, { bg: string; text: string }>;
}

export type DropdownCell = CustomCell<DropdownCellProps>;

export const emptyDropdownCellValue: Option = {
  label: "",
  value: "",
};

// Module-level mousedown handler registered at import time — before any React
// component (including glide-data-grid's ClickOutsideContainer) mounts.
// When a mousedown targets a combobox listbox/option rendered in a portal,
// it marks the portal root with "click-outside-ignore" so that
// ClickOutsideContainer skips its close-on-outside-click behaviour.
if (typeof document !== "undefined") {
  document.addEventListener(
    "mousedown",
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!target?.closest) return;

      if (target.closest('[role="listbox"], [role="option"]')) {
        let el: HTMLElement = target;

        while (el.parentElement && el.parentElement !== document.body) {
          el = el.parentElement;
        }

        el.classList.add("click-outside-ignore");
        // Clean up after the current event cycle so normal outside-clicks
        // still close the editor on subsequent interactions.
        setTimeout(() => el.classList.remove("click-outside-ignore"), 0);
      }
    },
    true, // capture phase — fires before ClickOutsideContainer's handler
  );
}

const DropdownCellEdit: ReturnType<ProvideEditorCallback<DropdownCell>> = ({
  value: cell,
  onFinishedEditing,
}) => {
  const [data, setData] = React.useState<Option[]>([]);

  const getChoices = React.useCallback(
    async (text: string) => {
      setData((await cell.data?.update?.(text)) ?? []);
    },
    [cell.data],
  );

  const userProps = pick(cell.data, ["allowCustomValues", "emptyOption"]);
  const props = cell.data.update
    ? { fetchOnFocus: true, fetchChoices: getChoices, choices: data }
    : { fetchOnFocus: false, fetchChoices: () => Promise.resolve([]), choices: cell.data.choices };

  return (
    <Combobox
      allowCustomValues={userProps.allowCustomValues}
      alwaysFetchOnFocus={props.fetchOnFocus}
      allowEmptyValue={userProps.emptyOption}
      fetchMore={{
        hasMore: false,
        loading: false,
        onFetchMore: () => undefined,
      }}
      options={props.choices ?? []}
      value={cell.data.value}
      fetchOptions={props.fetchChoices}
      loading={false}
      name=""
      onChange={event => {
        return onFinishedEditing({
          ...cell,
          data: {
            ...cell.data,
            value: props.choices?.find(c => c.value === event.target.value) ?? {
              label: event.target.value ?? "",
              value: event.target.value ?? "",
            },
          },
        });
      }}
    />
  );
};

export const dropdownCellRenderer: CustomRenderer<DropdownCell> = {
  kind: GridCellKind.Custom,
  isMatch: (c): c is DropdownCell => (c.data as any).kind === "dropdown-cell",
  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, pillColors } = cell.data;
    const label = value?.label ?? "";
    const pillColor = pillColors?.[value?.value ?? ""];

    if (pillColor) {
      // ── Pill rendering ──
      const hPad = 10;
      const pillH = 20;
      const pillY = rect.y + (rect.height - pillH) / 2;
      const pillX = rect.x + 8;
      const textWidth = ctx.measureText(label).width;
      const pillW = textWidth + hPad * 2;
      const r = pillH / 2;

      ctx.fillStyle = pillColor.bg;
      ctx.beginPath();
      ctx.moveTo(pillX + r, pillY);
      ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, r);
      ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, r);
      ctx.arcTo(pillX, pillY + pillH, pillX, pillY, r);
      ctx.arcTo(pillX, pillY, pillX + pillW, pillY, r);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = pillColor.text;
      ctx.fillText(
        label,
        pillX + hPad,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme),
      );
    } else {
      // ── Plain text rendering ──
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        label,
        rect.x + 8,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme),
      );
    }

    // Draw a downward chevron on the right for editable cells
    if (!cell.readonly) {
      const cx = rect.x + rect.width - 14;
      const cy = rect.y + rect.height / 2;
      const w = 6;
      const h = 4;

      ctx.fillStyle = theme.textLight ?? "#9ca3af";
      ctx.beginPath();
      ctx.moveTo(cx - w / 2, cy - h / 2);
      ctx.lineTo(cx + w / 2, cy - h / 2);
      ctx.lineTo(cx, cy + h / 2);
      ctx.closePath();
      ctx.fill();
    }

    return true;
  },
  provideEditor: () => ({
    editor: DropdownCellEdit,
    disablePadding: true,
    deletedValue: cell => ({
      ...cell,
      copyData: "",
      data: {
        ...cell.data,
        display: "",
        value: null,
      },
    }),
  }),
  onPaste: (value, data) => ({
    ...data,
    value: value ? { value, label: value } : null,
  }),
};
